import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DEFAULT_TRADING,
  brokerageFor,
  marginModelOf,
  requiredMargin,
} from '@dabba/contracts';
import type { FillOutcome, OrderType, ProductType, Side } from '@dabba/contracts';
import { api, ApiError } from '../api/client';
import { useSession } from '../store/session';
import { usePricesLive, useTick } from '../data/DataProvider';
import { useInvalidateTrading, useMarketStatus } from '../data/hooks';
import { instrumentTitle, kindFromSymbol, orderTypeLabel } from '../lib/instrumentLabel';
import { num, rupee, rupee0 } from '../lib/format';
import { toast } from './toast';
import { IconClose } from './icons';
import { KV, Segment } from './common';
import { Change, LiveNum } from './pro';

export type TicketRequest = {
  symbol: string;
  side?: Side;
  /** Exit mode: preset the closing side and the full held quantity. */
  exit?: boolean;
  product?: ProductType;
};

type TicketCtx = { openTicket: (req: TicketRequest) => void };
const Ctx = createContext<TicketCtx | null>(null);

export function useOrderTicket(): TicketCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useOrderTicket must be used within <OrderTicketProvider>');
  return c;
}

const ORDER_TYPES: OrderType[] = ['Market', 'Limit', 'SL', 'SL-M'];

const TYPE_HINTS: Record<OrderType, string> = {
  Market: 'Fills immediately at the best available price.',
  Limit: 'Rests until price reaches your limit, then fills at it or better.',
  'SL': 'Stoploss Limit — at your trigger, a limit order is placed.',
  'SL-M': 'Stoploss Market — at your trigger, a market order is placed.',
};

function Ticket({ req, onClose }: { req: TicketRequest; onClose: () => void }) {
  const navigate = useNavigate();
  const token = useSession((s) => s.token);
  const status = useSession((s) => s.status);
  const invalidate = useInvalidateTrading();

  const { symbol } = req;
  const isExit = req.exit === true;

  const [side, setSide] = useState<Side>(req.side ?? 'BUY');
  const [orderType, setOrderType] = useState<OrderType>('Market');
  const [product, setProduct] = useState<ProductType>(req.product ?? 'MIS');
  const [qty, setQty] = useState(0);
  const [qtyInput, setQtyInput] = useState('');
  const [limitInput, setLimitInput] = useState('');
  const [triggerInput, setTriggerInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [exitPrefilled, setExitPrefilled] = useState(false);

  const { data: meta } = useQuery({
    queryKey: ['instrumentMeta', symbol],
    queryFn: () => api.instrument(symbol),
    staleTime: 60 * 60_000,
    retry: 1,
  });
  const { data: portfolio } = useQuery({
    queryKey: ['portfolio'],
    queryFn: api.portfolio,
    enabled: !!token,
  });
  const { data: positions } = useQuery({
    queryKey: ['positions'],
    queryFn: api.positions,
    enabled: !!token,
  });
  // The rules the engine will actually apply — so the quote matches the debit.
  const { data: cfg } = useQuery({
    queryKey: ['tradingConfig'],
    queryFn: api.tradingConfig,
    enabled: !!token,
    staleTime: 5 * 60_000,
  });
  const trading = cfg ?? DEFAULT_TRADING;

  const tick = useTick(symbol);
  const market = useMarketStatus();
  const pricesLive = usePricesLive();

  // ---- instrument classification ----
  const kind = meta?.kind ?? kindFromSymbol(symbol);
  const isFno = kind === 'future' || kind === 'option';
  // An index ticks but is not tradable — only its derivatives are. The server
  // enforces this too; the ticket just refuses to pretend otherwise.
  const isIndex = kind === 'index';
  const lot = isFno ? Math.max(1, meta?.lotSize ?? 1) : 1;

  const heldQty = positions?.find((p) => p.symbol === symbol && p.product === product)?.qty ?? 0;
  const maxQty = isExit ? Math.abs(heldQty) : Infinity;

  // Exit mode fills itself in once, after positions land.
  useEffect(() => {
    if (!isExit || exitPrefilled || heldQty === 0) return;
    setExitPrefilled(true);
    setSide(heldQty > 0 ? 'SELL' : 'BUY');
    setQty(Math.abs(heldQty));
    setQtyInput(String(Math.abs(heldQty)));
  }, [isExit, exitPrefilled, heldQty]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ---- pricing ----
  const ltp = tick?.ltp ?? 0;
  const hasPrice = tick != null;
  const { bid, ask } = tick ?? {};
  // A market order pays the spread: buys lift the ask, sells hit the bid.
  const takerPrice = side === 'BUY' ? (ask ?? ltp) : (bid ?? ltp);
  const needsPrice = orderType === 'Limit' || orderType === 'SL';
  const needsTrigger = orderType === 'SL' || orderType === 'SL-M';
  const limitPrice = parseFloat(limitInput) || undefined;
  const triggerPrice = parseFloat(triggerInput) || undefined;
  const effectivePrice = needsPrice ? (limitPrice ?? ltp) : takerPrice;
  // The SAME functions the engine margins and charges with (@dabba/contracts),
  // fed the user's own leverage/brokerage config — so this quote and the debit
  // can never tell different stories. Options are asymmetric: buying costs the
  // premium, writing costs ≈SPAN; equity/futures are leveraged.
  const marginModel = marginModelOf(meta?.kind, side);
  const marginRequired = requiredMargin({
    model: marginModel,
    qty,
    refPrice: effectivePrice,
    strike: meta?.strike ?? 0,
    cfg: trading,
  });
  const estCharges = brokerageFor({ cfg: trading, qty, lotSize: lot });

  // ---- funds ----
  const fundsKnown = portfolio?.availableFunds != null;
  const available = portfolio?.availableFunds ?? 0;
  const delta = side === 'BUY' ? qty : -qty;
  // Closing or trimming frees margin rather than consuming it.
  const isReducing = heldQty !== 0 && Math.abs(heldQty + delta) < Math.abs(heldQty);
  const insufficientFunds = fundsKnown && !isReducing && marginRequired > available;
  const marketClosed = market != null && !market.tradable;

  const valid =
    qty > 0 &&
    hasPrice &&
    !isIndex &&
    !insufficientFunds &&
    !marketClosed &&
    fundsKnown &&
    (!isExit || (maxQty > 0 && qty <= maxQty)) &&
    (!needsPrice || limitPrice !== undefined) &&
    (!needsTrigger || triggerPrice !== undefined);

  /** One reason, the most blocking one first — never a wall of red. */
  const blocker: { text: string; tone: 'warn' | 'error' } | null = isIndex
    ? { text: 'Indices trade through their futures and options — pick a strike from the chain.', tone: 'warn' }
    : marketClosed
      ? { text: `Market closed${market?.nextOpen ? ` — opens ${new Date(market.nextOpen).toLocaleString('en-IN', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}.`, tone: 'warn' }
      : !pricesLive
        ? { text: 'Prices are not live right now — order entry is paused until the feed resumes.', tone: 'warn' }
        : !hasPrice
          ? { text: 'Waiting for a live price for this symbol…', tone: 'warn' }
          : isExit && maxQty === 0
            ? { text: 'No open position left to exit.', tone: 'warn' }
            : insufficientFunds
              ? {
                  text: `Not enough funds — this needs ${rupee0(marginRequired)} and ${rupee0(available)} is available. Add funds or reduce the quantity.`,
                  tone: 'error',
                }
              : null;

  const setQtyFromInput = (raw: string): void => {
    const cleaned = raw.replace(/\D/g, '').slice(0, 6);
    setQtyInput(cleaned);
    const n = parseInt(cleaned, 10);
    setQty(Number.isFinite(n) && n > 0 ? n : 0);
  };

  /** F&O trades in whole lots; snap on blur rather than fighting the typist. */
  const snapQty = (): void => {
    const n = parseInt(qtyInput, 10);
    if (!Number.isFinite(n) || n <= 0) {
      setQty(0);
      setQtyInput('');
      return;
    }
    let snapped = isFno ? Math.max(lot, Math.round(n / lot) * lot) : n;
    if (maxQty !== Infinity) snapped = Math.min(snapped, maxQty);
    setQty(snapped);
    setQtyInput(String(snapped));
  };

  const products: Array<{ value: ProductType; label: string }> = isFno
    ? [
        { value: 'MIS', label: 'Intraday' },
        { value: 'NRML', label: 'Carry forward' },
      ]
    : [
        { value: 'MIS', label: 'Intraday' },
        { value: 'CNC', label: 'Delivery' },
      ];

  const showOutcome = (outcome: FillOutcome): void => {
    if (outcome.kind === 'filled') {
      toast({
        kind: 'filled',
        title: 'Order executed',
        detail: `${side} ${outcome.qty} ${symbol} at ${rupee(outcome.avgPrice)} · charges ${rupee(outcome.charges)}`,
      });
      onClose();
      navigate('/orders?tab=executed');
    } else if (outcome.kind === 'partial') {
      toast({
        kind: 'partial',
        title:
          outcome.filled === 0
            ? 'Order placed'
            : `Partly filled · ${outcome.filled} of ${outcome.total}`,
        detail:
          outcome.filled === 0
            ? `${orderTypeLabel(orderType)} order is resting — it fills when price crosses.`
            : 'The rest fills as liquidity allows.',
      });
      onClose();
      navigate('/orders?tab=open');
    } else {
      // Rejections keep the ticket open: the reason is usually something the
      // trader can fix right here (quantity, price, funds).
      toast({ kind: 'rejected', title: 'Order rejected', detail: outcome.reason });
    }
  };

  const onConfirm = async (): Promise<void> => {
    if (status !== 'authenticated') {
      onClose();
      navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    if (submitting || !valid) return;
    setSubmitting(true);
    try {
      const r = await api.placeOrder({
        symbol,
        side,
        orderType,
        product,
        qty,
        price: needsPrice ? limitPrice : undefined,
        triggerPrice: needsTrigger ? triggerPrice : undefined,
      });
      showOutcome(r.outcome);
      invalidate();
    } catch (e) {
      showOutcome({
        kind: 'rejected',
        reason: e instanceof ApiError ? e.message : 'Network error — the order was not placed.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const title = instrumentTitle(symbol, meta);
  // What this order is worth at the price it would go in at: the limit if one
  // is named, otherwise the last trade. Null rather than zero while neither
  // exists, so the row reads "—" instead of a confident ₹0.00.
  const valuePrice = needsPrice ? limitPrice : hasPrice ? ltp : undefined;
  const orderValue = qty > 0 && valuePrice ? qty * valuePrice : null;

  return (
    <div
      className="overlay overlay-center"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-label={`${side} ${title}`}>
        <div className="ticket-head">
          <div className="between">
            <div style={{ minWidth: 0 }}>
              <div className="t-lg semibold nowrap" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {title}
              </div>
              <div className="t-sm muted mt-1">
                {meta?.exchange ?? 'NSE'} ·{' '}
                {isFno ? `F&O · Lot ${lot}` : kind === 'index' ? 'Index' : 'Equity'}
              </div>
            </div>
            <button className="icon-btn" onClick={onClose} aria-label="Close">
              <IconClose />
            </button>
          </div>

          <div className="row mt-2 t-md">
            <LiveNum value={hasPrice ? ltp : undefined} className="semibold t-lg" />
            {tick ? <Change tick={tick} className="t-sm" /> : null}
            {bid != null && ask != null ? (
              <span className="ticket-touch">
                <span className="num up">{num(bid)}</span>
                <span className="label">spread {num(ask - bid)}</span>
                <span className="num down">{num(ask)}</span>
              </span>
            ) : null}
          </div>

          {!isExit ? (
            <div className="ticket-side mt-2">
              <button
                className={side === 'BUY' ? 'active-buy' : ''}
                onClick={() => setSide('BUY')}
                aria-pressed={side === 'BUY'}
              >
                Buy
              </button>
              <button
                className={side === 'SELL' ? 'active-sell' : ''}
                onClick={() => setSide('SELL')}
                aria-pressed={side === 'SELL'}
              >
                Sell
              </button>
            </div>
          ) : (
            <div className="banner banner-info mt-2">
              Closing your {heldQty > 0 ? 'long' : 'short'} position · holding {maxQty}
            </div>
          )}
        </div>

        <div className="modal-body">
          {!isExit ? (
            <div className="between mb-3">
              <span className="t-sm muted">Product</span>
              <Segment value={product} onChange={setProduct} items={products} />
            </div>
          ) : null}

          <div className="grid-2">
            <div className="field">
              <label htmlFor="tk-qty">Quantity{isFno ? ` (lot ${lot})` : ''}</label>
              <input
                id="tk-qty"
                className="input num"
                inputMode="numeric"
                placeholder={String(lot)}
                value={qtyInput}
                onChange={(e) => setQtyFromInput(e.target.value)}
                onBlur={snapQty}
              />
              {isExit && maxQty > 0 ? <span className="help-text">Max {maxQty}</span> : null}
            </div>
            <div className="field">
              <label htmlFor="tk-price">Limit price (₹)</label>
              <input
                id="tk-price"
                className="input num"
                inputMode="decimal"
                placeholder={needsPrice ? (hasPrice ? num(ltp) : '0.00') : 'At market'}
                value={needsPrice ? limitInput : ''}
                disabled={!needsPrice}
                onChange={(e) => setLimitInput(e.target.value.replace(/[^0-9.]/g, ''))}
              />
            </div>
          </div>

          {needsTrigger ? (
            <div className="field mt-2">
              <label htmlFor="tk-trigger">Trigger price (₹)</label>
              <input
                id="tk-trigger"
                className="input num"
                inputMode="decimal"
                placeholder="0.00"
                value={triggerInput}
                onChange={(e) => setTriggerInput(e.target.value.replace(/[^0-9.]/g, ''))}
              />
              <span className="help-text">The price at which your stoploss activates.</span>
            </div>
          ) : null}

          <div className="mt-3">
            <Segment
              value={orderType}
              onChange={setOrderType}
              items={ORDER_TYPES.map((t) => ({ value: t, label: orderTypeLabel(t) }))}
            />
            <div className="help-text mt-1">{TYPE_HINTS[orderType]}</div>
          </div>

          <div className="ticket-summary mt-3">
            <KV
              k={needsPrice ? 'Order value' : 'Order value (at last price)'}
              v={orderValue != null ? rupee(orderValue) : '—'}
            />
            <KV k="Margin required" v={rupee(marginRequired)} />
            <KV k="Estimated charges" v={rupee(estCharges)} />
            <hr className="divider" />
            <KV k="Available funds" v={fundsKnown ? rupee(available) : '…'} strong />
          </div>
          <div className="help-text mt-1">
            Charges are quoted here with the same function the server debits on the fill.
          </div>

          {blocker ? (
            <div className={`banner banner-${blocker.tone} mt-2`}>{blocker.text}</div>
          ) : null}
        </div>

        <div className="modal-foot">
          <button className="btn grow" onClick={onClose}>
            Cancel
          </button>
          <button
            className={`btn grow ${side === 'BUY' ? 'btn-buy' : 'btn-sell'}`}
            disabled={!valid || submitting}
            onClick={() => void onConfirm()}
          >
            {submitting ? 'Placing…' : `${side === 'BUY' ? 'Buy' : 'Sell'} ${qty > 0 ? qty : ''}`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
}

export function OrderTicketProvider({ children }: { children: ReactNode }) {
  const [req, setReq] = useState<TicketRequest | null>(null);
  const ctx = useMemo<TicketCtx>(() => ({ openTicket: setReq }), []);
  return (
    <Ctx.Provider value={ctx}>
      {children}
      {req ? (
        // Remount per request, so opening a fresh ticket never inherits the
        // quantity or order type left behind by the previous one.
        <Ticket
          key={`${req.symbol}:${req.side ?? ''}:${req.exit ? 'x' : ''}:${req.product ?? ''}`}
          req={req}
          onClose={() => setReq(null)}
        />
      ) : null}
    </Ctx.Provider>
  );
}
